import { useCallback } from "react"

import eq from "lodash-es/eq"
import isNumber from "lodash-es/isNumber"

import { generateUuid, reduxBatch } from "@/3514/utils"

import { useCoachingChatSocketEmitter as useEmitter, useCoachingChatSocketReceiver as useSocketReceiver } from "../api"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationMessagesPresenceSelector as useConversationMessagesPresenceSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatSocketEmitterEventName as EEventName,
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatMessageTransport as EMessageTransport,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatSocketRestartConversationEventPayload as TEventPayload,
    type TCoachingChatSocketConversationRestartedEventResponse as TRestartedEventResponse,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"

type TCoachingChatRestartConversationFunction = (participantMessageId?: number) => Promise<void>

function useCoachingChatConversationRestarter({
    entityId
}: Pick<_TSharedParams, "entityId">): TCoachingChatRestartConversationFunction {
    const conversationId: number | null = useConversationIdSelector(entityId)

    const { emit: emitter }: ReturnType<typeof useEmitter<TEventPayload>> = useEmitter<TEventPayload>(
        EEventName.RestartConversation
    )

    return useCallback(
        async (participantMessageId?: number): Promise<void> =>
            isNumber(participantMessageId)
                ? void emitter({
                      conversation_id: conversationId,
                      mode: "resume",
                      participant_message_id: participantMessageId
                  })
                : void emitter({ conversation_id: conversationId, mode: "resume" }),
        [conversationId, emitter]
    )
}

function useCoachingChatConversationRestartListener({
    entityId,
    entityMode,
    wasChatCompleted
}: Pick<_TSharedParams, "entityId" | "entityMode" | "wasChatCompleted">): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const areMessagesPresent: boolean = useConversationMessagesPresenceSelector(entityId)

    const isEnabled: boolean =
        eq(conversationApiMode, EConversationApiMode.Sockets) &&
        eq(entityMode, EEntityMode.Full) &&
        !wasChatCompleted &&
        isNumber(conversationId)

    const handler: (resp: TRestartedEventResponse) => void = useCallback(
        (resp: TRestartedEventResponse): void => {
            if (resp.conversation_id !== conversationId) return

            reduxBatch((): void => {
                conversationDispatcher.changeStatus(EConversationStatus.InProgress)
                conversationDispatcher.clearError()

                if (!areMessagesPresent) {
                    conversationDispatcher.markInput({ isDisabled: true, isDimmed: true })
                    conversationDispatcher.addMessage({
                        id: 1,
                        transport: EMessageTransport.Sockets,
                        author: EMessageAuthor.AI,
                        content: String(""),
                        isStreamable: true,
                        status: EConversationMessagePhase.Waiting,
                        clientMessageId: generateUuid(),
                        conversationId
                    })
                }
            })
        },
        [conversationId, conversationDispatcher, areMessagesPresent]
    )

    useSocketReceiver<TRestartedEventResponse>({
        isEnabled,
        event: ESocketListenerEventName.ConversationRestarted,
        handler
    })
}

export { useCoachingChatConversationRestarter, useCoachingChatConversationRestartListener }
export type { TCoachingChatRestartConversationFunction }
